import { User as SupabaseAuthUser, Session } from '@supabase/supabase-js';
import { supabase, inviteClient, TABLES } from '../supabase';
import { Role } from '../../types';
import { generateInvitePassword, getAvatarUrl } from '../../utils/formatters';
import { ERROR_MESSAGES, HTTP_STATUS } from '../../utils/constants';

// ============================================
// Auth Service
// ============================================

export const authApi = {
  /**
   * Sign in with email and password
   */
  async signIn(email: string, password: string): Promise<{ user: SupabaseAuthUser | null; session: Session | null }> {
    const { data, error } = await supabase.auth.signInWithPassword({ 
      email, 
      password 
    });
    
    if (error) throw error;
    return data;
  },

  /**
   * Sign out the current user
   */
  async signOut(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  /**
   * Get current session
   */
  async getSession(): Promise<{ user: SupabaseAuthUser | null; session: Session | null }> {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    return {
      user: data.session?.user || null,
      session: data.session,
    };
  },

  /**
   * Update user password
   */
  async updatePassword(password: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({ 
      password,
      data: { is_invited: false }
    });
    if (error) throw error;
  },

  /**
   * Invite a new user (Admin function)
   * Creates both auth user and users table record
   */
  async inviteUser(email: string, name: string, role: Role): Promise<SupabaseAuthUser | null> {
    const tempPassword = generateInvitePassword();
    
    // Step 1: Create auth user
    // Redirect to base URL - main.tsx will handle the auth callback and redirect to set-password
    const redirectUrl = window.location.origin;
    
    const { data, error } = await inviteClient.auth.signUp({
      email,
      password: tempPassword,
      options: {
        data: { 
          full_name: name, 
          role: role, 
          is_invited: true 
        },
        emailRedirectTo: redirectUrl,
      },
    });

    if (error) {
      // Translate common error codes
      if (error.message.includes(String(HTTP_STATUS.UNPROCESSABLE_ENTITY))) {
        throw new Error(ERROR_MESSAGES.AUTH.SIGNUP_DISABLED);
      }
      throw error;
    }

    // Step 2: Create users table record
    if (data.user) {
      const { error: dbError } = await supabase
        .from(TABLES.USERS)
        .insert({
          id: data.user.id,
          email: email,
          name: name,
          role: role,
          avatar: getAvatarUrl(data.user.id),
        });

      if (dbError) {
        console.error('Failed to create user record:', dbError);
        // Don't throw - auth user was created, they can still log in
        // The profile will be created on first login if missing
      }
    }

    return data.user;
  },

  /**
   * Subscribe to auth state changes
   */
  onAuthStateChange(callback: (event: string, session: Session | null) => void) {
    return supabase.auth.onAuthStateChange((event, session) => {
      callback(event, session);
    });
  },
};

export default authApi;
